import React from "react";
import {
  FiBookOpen,
  FiCalendar,
  FiCheckSquare,
  FiCreditCard,
  FiHome,
  FiInfo,
  FiUsers,
} from "react-icons/fi";
import ParentActionsSubnav, { ParentActionsView } from "./components/ParentActionsSubnav";

interface ParentPortalDesktopShellProps {
  tabItems: Array<{ key: string; label: string }>;
  activeTab: string;
  setActiveTab: (key: any) => void;
  summaryCards?: Array<any>;
  children: React.ReactNode;
  actionsView: ParentActionsView;
  setActionsView: (view: ParentActionsView) => void;
  openActionsCount: number;
  schoolTasksCount: number | null;
  showSchoolTasks: boolean;
  openActionsLabel: string;
  schoolTasksLabel: string;
}

const iconForTab: Record<string, React.ComponentType<{ size?: number; "aria-hidden"?: boolean }>> = {
  overview: FiInfo,
  info: FiInfo,
  actions: FiCheckSquare,
  family: FiUsers,
  children: FiUsers,
  calendar: FiCalendar,
  finance: FiCreditCard,
  finances: FiCreditCard,
  directory: FiBookOpen,
};

const ParentPortalDesktopShell = ({
  tabItems,
  activeTab,
  setActiveTab,
  children,
  actionsView,
  setActionsView,
  openActionsCount,
  schoolTasksCount,
  showSchoolTasks,
  openActionsLabel,
  schoolTasksLabel,
}: ParentPortalDesktopShellProps) => {
  return (
    <section className="hidden w-full overflow-hidden rounded-[22px] border border-slate-200/70 bg-white shadow-[0_24px_60px_-38px_rgba(15,23,42,0.35)] lg:block dark:border-slate-800 dark:bg-slate-950">
      <nav
        className="flex w-full items-stretch justify-between gap-1 border-b border-slate-200/70 px-6 pt-4 dark:border-slate-800"
        aria-label="Parent portal sections"
      >
        {tabItems.map((tab) => {
          const isActive = activeTab === tab.key;
          const Icon = iconForTab[tab.key] || FiHome;

          const tabButton = (
            <button
              key={tab.key}
              type="button"
              onClick={() => tab.key === "actions" ? setActionsView("open") : setActiveTab(tab.key)}
              className={[
                "relative flex min-h-[4.2rem] min-w-0 flex-1 flex-col items-center justify-center gap-1.5 px-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70",
                isActive
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-slate-500 hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300",
              ].join(" ")}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon size={20} aria-hidden />
              <span>{tab.label}</span>
              <span
                className={[
                  "absolute bottom-0 left-1/2 h-0.5 w-12 -translate-x-1/2 rounded-full transition",
                  isActive ? "bg-emerald-600" : "bg-transparent",
                ].join(" ")}
                aria-hidden="true"
              />
            </button>
          );

          if (tab.key !== "actions") return tabButton;

          return (
            <div key={tab.key} className={`flex min-w-0 items-stretch transition-[flex-grow] duration-200 ${isActive && showSchoolTasks ? "flex-[1.8]" : "flex-1"}`}>
              {tabButton}
              {isActive && (
                <ParentActionsSubnav
                  activeView={actionsView}
                  onChange={setActionsView}
                  openActionsCount={openActionsCount}
                  schoolTasksCount={schoolTasksCount}
                  showSchoolTasks={showSchoolTasks}
                  openActionsLabel={openActionsLabel}
                  schoolTasksLabel={schoolTasksLabel}
                  className="max-w-[10rem] pb-1"
                />
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-6">{children}</div>
    </section>
  );
};

export default ParentPortalDesktopShell;
